import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { services } from "@/lib/services-data";
import { ArrowRight, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useQuoteForm } from "@/components/QuoteFormContext";

const ServicesSection = () => {
  const { openQuoteForm } = useQuoteForm();

  return (
    <section id="services" className="relative -mt-px section-padding bg-background section-glow overflow-hidden">
      <div className="container-max relative">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8 mb-14 max-w-[1200px] mx-auto">
          <div className="max-w-2xl">
            <motion.span
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="eyebrow-pill mb-5"
            >
              What We Offer
            </motion.span>
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="font-heading text-foreground tracking-tight font-bold leading-[1.05]"
              style={{ fontSize: "clamp(2.25rem, 4.5vw, 4rem)" }}
            >
              Our <span className="text-gradient-primary">Services</span>
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 15 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="text-muted-foreground max-w-[560px] text-lg leading-relaxed mt-5"
            >
              From privacy and picket fences to chain link, aluminum, and construction
              perimeters — explore the full range of fencing we install and repair across
              Dillon and Summit County.
            </motion.p>
          </div>
          <Button asChild size="lg" variant="outline" className="self-start lg:self-end gap-2">
            <Link to="/services">View All Services <ArrowRight className="h-4 w-4" /></Link>
          </Button>
        </div>

        <div className={`grid grid-cols-1 sm:grid-cols-2 gap-7 max-w-[1200px] mx-auto justify-items-center ${services.length === 4 ? "lg:grid-cols-2" : "lg:grid-cols-3 [&>*:last-child:nth-child(3n-2)]:lg:col-start-2"}`}>
          {services.map((service, i) => (
            <motion.article
              key={service.slug}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.08, duration: 0.55, ease: [0.33, 1, 0.68, 1] }}
              className="group w-full flex flex-col card-premium p-3"
            >
              <Link to={`/services/${service.slug}`} className="block">
                <div className="media-frame" style={{ aspectRatio: "16 / 10" }}>
                  <img src={service.image} alt={service.imageAlt} loading="lazy" />
                  <span className="corner tl" /><span className="corner tr" />
                  <span className="corner bl" /><span className="corner br" />
                  <span className="media-icon"><Plus className="h-5 w-5" strokeWidth={2.25} /></span>
                  <span className="absolute top-4 left-4 z-[3] inline-flex items-center rounded-full bg-white/95 text-foreground text-[11px] font-semibold uppercase tracking-wider px-3 py-1 border border-black/5">
                    {service.price}
                  </span>
                </div>
              </Link>
              <div className="px-2 pt-6 pb-3 flex flex-col flex-1">
                <h3 className="font-heading text-2xl text-foreground tracking-tight font-bold leading-tight group-hover:text-primary transition-colors">
                  {service.title}
                </h3>
                <p className="text-muted-foreground text-sm mt-2 leading-relaxed flex-1">{service.subtitle}</p>
                <Link
                  to={`/services/${service.slug}`}
                  aria-label={`Learn more about ${service.title}`}
                  className="inline-flex items-center gap-2 text-primary font-semibold text-sm mt-5 hover:gap-3 transition-all"
                >
                  Learn more<span className="sr-only"> about {service.title}</span> <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="flex justify-center mt-14">
          <Button size="lg" onClick={openQuoteForm} className="gap-2 btn-glow">
            Get Free Estimate <ArrowRight className="h-5 w-5" />
          </Button>
        </div>
      </div>
    </section>
  );
};

export default ServicesSection;
